
import {
    acequiaReady,
    getUserId,
    getDeviceId,
    getUserKeys,
} from '../acequia/acequia.js'
import { jose } from '../deps.js'

export { jose }

// What this module is: the way a user signs a token for themselves
// (createUserToken). Verification is routeAuth.verifyBearer — the one
// verifier for every bearer. verifyToken and verifyDeviceToken (which
// trusted a payload's userId or iss, fetched that key and called the token
// verified: no exp, no scope, no revocation, the device model the server
// dropped 2026-08-20) and createToken (the pre-kid signer) were removed in
// 3.0.47 (unified-token-system, Phase 2).

// Default lifetime for user tokens (token-cookie-security, decided
// 2026-07-28). Renewal keeps live sessions going (cookie half-life
// refresh, SW mount re-mint, chain renewal engine); a leaked token dies
// on its own within 30 days instead of living forever.
export const DEFAULT_USER_TOKEN_TTL = 30 * 24 * 60 * 60 // 30 days in seconds

// The exp ceiling (direct-user-tokens-cannot-be-revoked, 2026-09-14): the
// server refuses any presentable token whose exp lies more than a year past
// the moment of verification (TOKEN_EXP_TOO_FAR), so minting one here would
// only produce a credential that never works. Mirrors MAX_TOKEN_TTL_SECONDS
// in localWebDAV/src/auth/requireExp.mjs.
export const MAX_USER_TOKEN_TTL = 365 * 24 * 60 * 60 // one year in seconds

// jose timespan strings ('30d', '1 year', '3600s') → seconds, so the ceiling
// can be checked before signing. Unknown shapes fall through to jose, which
// throws its own error for garbage.
const TIMESPAN = /^\s*(\d+(?:\.\d+)?)\s*(s|sec|secs|second|seconds|m|min|mins|minute|minutes|h|hr|hrs|hour|hours|d|day|days|w|week|weeks|y|yr|yrs|year|years)\s*$/i
const UNIT_SECONDS = { s: 1, m: 60, h: 3600, d: 86400, w: 604800, y: 365.25 * 86400 }
function timespanToSeconds(str) {
    const m = TIMESPAN.exec(str)
    if (!m) return null
    return Math.round(parseFloat(m[1]) * UNIT_SECONDS[m[2][0].toLowerCase()])
}

/**
 * Resolve an expiresTime argument against the default TTL.
 * - undefined/null → DEFAULT_USER_TOKEN_TTL
 * - number → seconds from now
 * - string → jose timespan (e.g. '365d'), passed through
 * 0 (the legacy no-expiry escape hatch) is refused: every presentable
 * token must carry an exp (require-exp-on-user-tokens, 2026-09-01).
 */
function resolveExpiration(expiresTime) {
    if (expiresTime === 0) {
        throw new Error(
            'expiresTime 0 (no expiry) is no longer supported — every user token must carry an exp; omit expiresTime for the default 30-day TTL'
        )
    }
    const ttl = expiresTime ?? DEFAULT_USER_TOKEN_TTL
    const seconds = typeof ttl === 'number' ? ttl : timespanToSeconds(ttl)
    if (seconds !== null && seconds > MAX_USER_TOKEN_TTL) {
        throw new Error(
            `expiresTime ${ttl} exceeds the one-year ceiling — the server refuses tokens with exp more than a year out (TOKEN_EXP_TOO_FAR)`
        )
    }
    if (typeof ttl === 'number') return Math.floor(Date.now() / 1000) + ttl
    return ttl
}

/**
 * Create a user token for the new per-device user keys system.
 * The token includes kid in both payload and header for server verification.
 *
 * Uses getter functions to get current in-memory values (which may have been
 * updated by refreshUserIdentity after device linking).
 *
 * @param {Object} payload - Custom claims to include in token
 * @param {number} expiresTime - Expiration time in seconds from now
 * @returns {Promise<string>} Signed JWT
 */
export async function createUserToken(payload, expiresTime) {
    // Wait for acequia to be ready first
    await acequiaReady()

    // Use getter functions to get current values (not cached promise values)
    const userId = getUserId()
    const deviceId = getDeviceId()
    const userKeys = getUserKeys()

    // No user keys means nothing can sign: createToken (the pre-kid
    // device-key signer this used to fall back to) was removed in 3.0.47,
    // so say what's wrong instead of throwing a ReferenceError.
    if (!userKeys) {
        throw new Error(
            'No user keys on this device — register or link this device before signing a user token'
        )
    }

    const exp = resolveExpiration(expiresTime)

    // Export public key to calculate kid
    const userPublicJWK = await jose.exportJWK(userKeys.publicKey)
    const kid = await jose.calculateJwkThumbprint(userPublicJWK)

    let token = new jose.SignJWT({
        ...payload,
        kid, // Key identifier for lookup
        deviceId, // Device attribution
    })
        .setSubject(userId)
        .setIssuedAt()
        .setIssuer(location.hostname)
        .setProtectedHeader({
            alg: 'PS256',
            kid,
        })

    token.setExpirationTime(exp)
    return await token.sign(userKeys.privateKey)
}
